import { captureMessage } from '@sentry/browser';
import * as Conversation from 'src/replicant/conversation';
import {
  ConversationalScript,
  DescriptiveScript,
  DialogData,
  DialogID,
  getNextScriptId,
  Script,
  ScriptID,
  SelectionalScript,
} from 'src/replicant/conversation/conservation.ruleset';
import { DialogScene } from 'src/replicant/data/Dialogs.schema';
import {
  getRandomItemFromArray,
  weightedRandom,
} from 'src/replicant/utils/random';
import StateObserver, { now } from 'src/StateObserver';
import { PetEntity } from '../logic/EntityManager';
import type { PetController } from '../pet/PetController';
import {
  trackDialogAnswerEvent,
  trackDialogStartEvent,
  trackDialogueContinueEvent,
  type DialogTriggerType,
} from 'src/replicant/conversation/conversation.analytics';
import { PetSpeciesID, PetState } from 'src/replicant/pet';
import * as Pet from 'src/replicant/pet';
import PopupMonitor from '../logic/PopupMonitor';
import { getTutorialController } from '../tutorial/TutorialController';
import { openPopupPromise } from 'src/lib/popups/popupOpenClose';
import {
  clearTempVisibleStats,
  setLockMenu,
  setTempVisibleStats,
} from 'src/state/ui';
import { captureGenericError } from 'src/lib/sentry';
import { isSceneEntered } from 'src/lib/stateUtils';
import * as Fate from 'src/replicant/fate';
import * as Visit from 'src/replicant/visit';
import * as Tutorial from 'src/replicant/tutorial';
import features from 'src/replicant/ruleset/features';
import { getMutex } from 'src/replicant/utils/mutex';
import { petIdleSequence } from '../pet/pet.sequence';
import { openAddFriendPopup } from '../friends/addFriendsV2.sequence';
import { openAddFriendDirectShare } from './wantToInvite.share';
import {
  appendToConversationData,
  getDialogs,
  getScripts,
  getValidDialogs,
  shouldShowPlayMatch2Dialog,
} from './conversation.utils';
import { getPetConversationAssetPaths } from './conversation.assets';
import { petSpeciesAssets } from 'src/loadingGroups';
import { waitForAssets } from 'src/lib/utils';
import { duration } from 'src/replicant/utils/duration';
import { Feature } from 'src/replicant/analytics';
import { playMatch2 } from '../minigame/match2-timestep/match2.play';
import { isPlayingMatch2Sequence } from '../minigame/match2-timestep/match2.sequence';
import { track } from 'src/lib/analytics/utils';
import { dreamDriftIsPlaying } from 'src/game/dreamDrift/dreamDrift.utils';

export type WantToAddFriendResult = 'addFriend' | 'maybeLater';

/**
 * Analytics context captured when a dialog is selected.
 */
type DialogAnalyticsContext = {
  dialogScene: DialogScene;
  petSpecies: PetSpeciesID;
  petState: PetState;
  triggerType: DialogTriggerType;
};

/**
 * Resolve the most specific trigger classification for a dialog.
 *
 * @param dialogScene Scene used to select the dialog.
 * @param petState Pet state used to select the dialog.
 * @param isLocalPet Whether the speaking pet belongs to the current player.
 * @param origin General tap or automatic origin of the dialog.
 * @returns Trigger classification recorded in analytics.
 */
function getDialogTriggerType(
  dialogScene: DialogScene,
  petState: PetState,
  isLocalPet: boolean,
  origin: DialogTriggerType,
): DialogTriggerType {
  // Care blockers are a distinct response to a blocked player action.
  if (dialogScene === 'care_action_blocker') return 'care_blocker';

  // Evolution dialogs can use either lifecycle scenes or the evolving state.
  const isEvolutionDialog =
    petState === 'evolving' ||
    dialogScene === 'before_evolution' ||
    dialogScene === 'after_evolution';
  if (isEvolutionDialog) return 'evolution';

  // Friend, duo, and recent-visitor dialogs belong to the visitor flow.
  const isVisitorDialog =
    !isLocalPet ||
    dialogScene === 'had_visitor' ||
    dialogScene.startsWith('friend_') ||
    dialogScene.startsWith('duo_');
  if (isVisitorDialog) return 'visitor';

  // Other dialogs retain their original manual or automatic trigger.
  return origin;
}

/**
 * The `PetConversationController` class manages the conversation interactions between the user and their pets.
 * It ensures that only one pet can have a dialog interaction with the user at a time using a mutex.
 * The class also handles the initialization, starting, pausing, and destruction of the conversation system.
 * It provides methods to check if a conversation is in progress, wait for a conversation to finish, and handle pet tap events.
 * Additionally, it manages the display of dialogs and scripts, and triggers specific actions based on the dialog state.
 */
export class PetConversationController {
  /**
   * We could have many pets but only one could have dialog interaction with user at time
   */
  private static conversationMutex = getMutex();

  /**
   * Set to true after the user has seen the play match2 dialog.
   * We wan tot show this dialog only once per session.
   */
  public hasShownPlayMatch2InSession = false;

  /**
   * Set to true if the PET auto dialog SHOULD BE BLOCKED.
   */
  private autoDialogBlocked = false;

  private active = false;
  private activeDialog: DialogID | undefined;
  private activeScript: ScriptID | undefined;
  private wantToAddFriendResult: WantToAddFriendResult | undefined;

  protected entity: PetEntity;

  constructor(protected pet: PetController) {
    this.entity = pet.entity;
  }

  /**
   * Pet created
   */
  public init(activeDialog: DialogID | null) {
    const { user } = StateObserver.getState();

    // TODO:  Make this blocking operation somehow
    void this.showDialog(activeDialog);

    // Listen pet tapped event
    this.entity.on(PetEntity.TapEvent, async () => this.handlePetTapped());

    // Don't start conversation system until completing basic gameplay tutorial
    if (Tutorial.getters.isTutorialStepComplete(user, 'basicGameplay')) {
      this.start();
    }
  }

  /**
   * Conversation controller active and can start new conversations
   */
  public start() {
    this.active = true;
  }

  /**
   * Conversation controller on pause and can't start new conversations
   */
  public pause() {
    this.active = false;
  }

  /**
   * Pet destroyed
   */
  public destroy() {
    this.entity.removeAllListeners(PetEntity.TapEvent);
  }

  /**
   * Return true if conversation in progress
   */
  public inProgress(): boolean {
    if (this.conversationMutex.isLocked()) return true;

    if (
      PopupMonitor.isOpen('popupDescriptiveScript') ||
      PopupMonitor.isOpen('popupSelectionalScript') ||
      PopupMonitor.isOpen('popupConversationScript') ||
      PopupMonitor.isOpen('popupSpeechBubble')
    ) {
      return true;
    }

    return !!this.activeDialog;
  }

  /**
   * Get conversation global mutex
   */
  private get conversationMutex() {
    return PetConversationController.conversationMutex;
  }

  /**
   * Return active script
   */
  public getActiveScript(): ScriptID | undefined {
    return this.activeScript;
  }

  /**
   * Resolved when all conversation was finished
   */
  public async waitForFinish() {
    await this.conversationMutex.lock();
    this.conversationMutex.release();
  }

  /**
   * Handle a manual or automatic request to start a pet dialog.
   *
   * @param triggerType General origin of the dialog request.
   * @param retry Whether missing conversation assets should be loaded and retried.
   * @returns A promise that resolves after the selected dialog finishes.
   */
  public async handlePetTapped(
    triggerType: DialogTriggerType = 'tap',
    retry = true,
  ): Promise<void> {
    // Conversation system on pause
    if (!this.active) return;

    // Prevent pet tap during match2 game sequence
    if (isPlayingMatch2Sequence()) {
      return;
    }

    // If any conversation in progress
    if (this.inProgress()) {
      return;
    }

    // Conversation mutex is locked, ignore click then
    if (this.conversationMutex.isLocked()) {
      return;
    }

    // Is pet ready for conversation
    if (!this.isInConversationScene() || !this.pet.isIdle()) {
      return;
    }

    const state = StateObserver.getState();
    const { user } = state;

    const { state: status, species, id } = this.pet;

    if (!species) {
      captureMessage(
        `Trying to start conversation when pet has no species.`,
        'warning',
      );
      return;
    }

    const { hasPermanentStatusEffect } = Fate.getters;
    const isVisiting = Visit.getters.isVisiting(user);
    const isLocalPet = this.pet.isLocalPet();

    // If selected pet is not local pet and visiting, don't show tap dialog if pet is dead
    // It will play its own dead dialog automatically after transition complete.
    if (!isLocalPet && this.pet.state === 'dead') {
      return;
    }

    let dialogScene: DialogScene;

    if (
      !isVisiting &&
      hasPermanentStatusEffect(user, 'hadVisitor', StateObserver.now())
    ) {
      // If pet dead handle a visitor as if the pet has already played had_visitor dialog
      if (hasPermanentStatusEffect(user, 'dead', StateObserver.now())) {
        StateObserver.invoke.acknowledgeVisit();
        dialogScene = isLocalPet ? 'normal_chat' : 'friend_chat';
      } else {
        dialogScene = 'had_visitor';
      }
    } else {
      dialogScene = isLocalPet ? 'normal_chat' : 'friend_chat';
    }

    // Check if should show match2 auto dialog, only if it's main users pet and not visiting
    const showPlayMatch2 =
      isLocalPet && !isVisiting && shouldShowPlayMatch2Dialog(state);
    if (showPlayMatch2) {
      this.hasShownPlayMatch2InSession = true;
      dialogScene = 'normal_play_match';
    }

    const dialogOptions = getValidDialogs(
      user,
      dialogScene,
      species,
      status,
      isLocalPet
        ? user.conversation.answers
        : (user.visit.visitSession?.answers ?? {}),
    );

    // Lock menu during conversation
    StateObserver.dispatch(setLockMenu(true));

    if (!dialogOptions || dialogOptions.length === 0) {
      // If no dialogs found for current pet species, try to load assets and try again.
      if (retry) {
        await waitForAssets(petSpeciesAssets[species], {
          feature: Feature.PET._,
          subFeature: Feature.PET.ASSETS,
        });
        appendToConversationData(species);
        return this.handlePetTapped(triggerType, false);
      }

      // Otherwise, log an error and return.
      const message = `Dialog not found for scene:${dialogScene} species:${species} state:${status}`;
      captureGenericError(message, new Error('Dialog not found'));
      // Unlock menu on error
      StateObserver.dispatch(setLockMenu(false));
      return;
    }

    // Prioritize dialogs with required answers.
    const weights = dialogOptions.map((dialog) =>
      !isLocalPet && dialog.req_answers ? 100 : 1,
    );
    const dialog = dialogOptions[weightedRandom(weights, Math.random)];
    StateObserver.dispatch(setTempVisibleStats(id));

    // Show dialogs
    await this.showDialog(dialog.id, {
      dialogScene,
      petSpecies: species,
      petState: status,
      triggerType,
    });

    // Unlock menu after conversation
    StateObserver.dispatch(setLockMenu(false));

    // Show minigame popup if needed.
    if (showPlayMatch2) {
      // Dream Drift or another popup can open while this dialog is pending.
      if (dreamDriftIsPlaying() || PopupMonitor.isAnyPopupOpen()) return;

      // The match2.time is the start time of last match2 game.
      const hoursSinceLastMatchGame = user.match2.time
        ? (now() - user.match2.time) / duration({ minutes: 60 })
        : undefined;

      // Send the analytics event for the pet triggering the puzzle.
      track('PetTriggerPuzzle', {
        isForced: false,
        dialogID: dialog.id,
        hoursSinceLastMatchGame,
      });

      await playMatch2({ origin: undefined });
    }
  }

  /**
   * Play pet conversation dialog by scene
   * @param args
   * @returns
   */
  public async playConversation(args: {
    dialogScene: DialogScene;
    retry: boolean;
  }): Promise<void> {
    const state = StateObserver.getState();
    const { user } = state;
    const { state: status, species, id } = this.pet;
    const { dialogScene, retry } = args;

    if (!species) {
      captureMessage(
        `Trying to play conversation when pet has no species.`,
        'warning',
      );
      return;
    }

    const isLocalPet = this.pet.isLocalPet();
    const dialogOptions = getValidDialogs(
      user,
      dialogScene,
      species,
      status,
      isLocalPet
        ? user.conversation.answers
        : (user.visit.visitSession?.answers ?? {}),
    );

    if (!dialogOptions || dialogOptions.length === 0) {
      // If no dialogs found for current pet species, try to load assets and try again.
      if (retry) {
        await waitForAssets(petSpeciesAssets[species], {
          feature: Feature.PET._,
          subFeature: Feature.PET.ASSETS,
        });
        appendToConversationData(species);
        return this.playConversation({ dialogScene, retry: false });
      }

      // Otherwise, log an error and return.
      const message = `Dialog not found for scene:${dialogScene} species:${species} state:${status}`;
      captureGenericError(message, new Error('Dialog not found'));
      return;
    }

    // Prioritize dialogs with required answers.
    const weights = dialogOptions.map((dialog) =>
      !isLocalPet && dialog.req_answers ? 100 : 1,
    );
    const dialog = dialogOptions[weightedRandom(weights, Math.random)];
    StateObserver.dispatch(setTempVisibleStats(id));

    // Show dialogs
    await this.showDialog(dialog.id, {
      dialogScene,
      petSpecies: species,
      petState: status,
      triggerType: 'auto',
    });
  }

  /**
   * Start conversation dialog
   */
  public async begin(
    scene: DialogScene,
    species: PetSpeciesID,
    status: PetState,
    retry = true,
  ): Promise<void> {
    if (!species) {
      captureMessage(
        `Trying to start conversation when pet has no species.`,
        'warning',
      );
      return;
    }

    const { user } = StateObserver.getState();

    const playerValidDialogs = getValidDialogs(
      user,
      scene,
      species,
      status,
      user.conversation.answers,
    );

    if (!playerValidDialogs?.length) {
      // If no dialogs found for current pet species, try to load assets and try again.
      if (retry) {
        await waitForAssets(petSpeciesAssets[species], {
          feature: Feature.PET._,
          subFeature: Feature.PET.ASSETS,
        });
        appendToConversationData(species);
        return this.begin(scene, species, status, false);
      }

      // Otherwise, log an error and return.
      captureMessage(
        `Dialog not found for scene:${scene} species:${species} state:${status}`,
        'warning',
      );
      return;
    }

    // Get random active dialog that suitable in current situation and pet and room
    const activeDialog = getRandomItemFromArray(
      playerValidDialogs,
      Math.random,
    ).id;

    // Show active dialog conversation
    await this.showDialog(activeDialog, {
      dialogScene: scene,
      petSpecies: species,
      petState: status,
      // Programmatic dialogs use auto unless their scene or state maps to a more specific trigger.
      triggerType: 'auto',
    });
  }

  /**
   * Show a dialog after waiting for any active conversation to finish.
   *
   * @param activeDialog Dialog to show, or null when no dialog is active.
   * @param analyticsContext Selection-time analytics metadata when available.
   * @returns A promise that resolves after the dialog finishes.
   */
  public async showDialog(
    activeDialog: DialogID | null,
    analyticsContext?: DialogAnalyticsContext,
  ) {
    // Wait for previous conversation finish
    await this.conversationMutex.lock();

    try {
      // Start new conversation
      await this.dialog(activeDialog, analyticsContext);
    } finally {
      // Release mutex
      this.conversationMutex.release();
    }
  }

  /**
   * Run one dialog and its complete script path.
   *
   * @param activeDialog Dialog to run, or null when no dialog is active.
   * @param analyticsContext Selection-time analytics metadata when available.
   * @returns A promise that resolves after the dialog finishes.
   */
  private async dialog(
    activeDialog: DialogID | null,
    analyticsContext?: DialogAnalyticsContext,
  ) {
    // No dialog
    if (!activeDialog) {
      this.activeDialog = undefined;
      this.activeScript = undefined;
      return;
    }

    // Track conversation state
    this.activeDialog = activeDialog;
    this.activeScript = undefined;
    this.wantToAddFriendResult = undefined;

    const { user, pet, level } = StateObserver.getState();
    const { getVisitedCount, getVisitsCount } = Visit.getters;

    const friendId = user.visit.visitSession?.friendId ?? '';
    const friendAttributes = pet[friendId]?.attributes;
    const friendAppearance = pet[friendId];
    const friendLevel = level[friendId];

    const dialog = getDialogs()[activeDialog];

    // Resolve analytics context from selection-time data or dialog fallbacks.
    const dialogScene =
      analyticsContext?.dialogScene ?? dialog?.scenes[0] ?? 'normal_chat';
    const petSpecies =
      analyticsContext?.petSpecies ??
      (dialog?.pet_id as PetSpeciesID | undefined) ??
      this.pet.species!;
    const petState = analyticsContext?.petState ?? this.pet.state;
    const triggerType = getDialogTriggerType(
      dialogScene,
      petState,
      this.pet.isLocalPet(),
      analyticsContext?.triggerType ?? 'auto',
    );
    const petEvolutionStage = Pet.ruleset.getEvolutionStage(
      'control',
      petSpecies,
    );
    const dialogVersion = getPetConversationAssetPaths(
      petSpecies,
      features(user).newPetDialogs,
    ).version;

    const props = {
      dialog_id: activeDialog,
      times_seen: Conversation.getters.getTimesSeen(user, activeDialog) + 1,
      isCareActionBlocked: dialogScene === 'care_action_blocker',
      petSpecies,
      petState,
      petEvolutionStage,
      triggerType,
      dialogVersion,
    };
    trackDialogStartEvent(
      !friendId
        ? { ...props, withFriends: false }
        : {
            ...props,
            withFriends: true,
            friendID: friendId,
            friendPetID: friendAppearance?.species,
            friendPetStomach: friendAttributes?.stomach,
            friendPetLove: friendAttributes?.love,
            friendDirtiness: friendAttributes?.dirtiness,
            friendUrine: friendAttributes?.urine,
            friendStress:
              friendAttributes && Pet.ruleset.getStressValue(friendAttributes),
            friendToothAcidity: friendAttributes?.toothAcidity,
            friendAge: friendAttributes?.age,
            friendPlayerLevel: friendLevel?.current,
            visits: getVisitsCount(user, friendId),
            visited: getVisitedCount(user, friendId),
          },
    );
    this.activeScript = dialog?.script_id ?? null;

    // No active script
    if (!this.activeScript) {
      this.activeDialog = undefined;
      this.activeScript = undefined;
      return;
    }

    // Trigger before dialog actions
    await this.onDialogOpen(dialog);

    // Update pet animation
    this.pet.appearance.updateState();

    // Show all script popups of current active dialog
    while (this.activeScript) {
      this.activeScript = await this.showActiveScriptPopup(
        this.activeScript,
        activeDialog,
      );
      this.pet.appearance.updateState();
    }

    await StateObserver.invoke.incrementDialogTimesSeen(activeDialog);

    // While visiting, the header UI switches over to whichever pet is currently talking and
    // then switches back when the conversation is over. To achieve this,
    // the tempVisibleStats Redux actions basically act as an override to the currently selected pet.
    StateObserver.dispatch(clearTempVisibleStats());

    // Dialog finished
    await this.onDialogFinish(dialog);

    // Cleanup
    this.activeDialog = undefined;
    this.activeScript = undefined;

    // Update pet animation
    this.pet.appearance.updateState();
  }

  /**
   * Show a script popup and track it in the active dialog.
   *
   * @param scriptID Script to display.
   * @param dialogId Parent dialog containing the script.
   * @returns The next script ID, when the dialog continues.
   */
  private async showActiveScriptPopup(
    scriptID: ScriptID,
    dialogId: DialogID,
  ): Promise<ScriptID | undefined> {
    const script = getScripts()[scriptID];
    if (!script) return undefined;

    // Track every displayed script through the common dispatch path.
    trackDialogueContinueEvent(script.id);

    const pet = this.pet;
    const { move_to: moveTo } = script;

    if (moveTo) {
      const { nextScene: scene } = StateObserver.getState().ui.transition;

      // Only move pet if in living room or friend's room.
      if (
        scene === 'livingRoom' ||
        scene === 'friendsLivingRoom' ||
        scene === 'arena'
      ) {
        await pet.moveTo({
          scene,
          location: moveTo,
        });
      }
    }

    const { scriptIsConversational, scriptIsDescriptive, scriptIsSelectional } =
      Conversation.ruleset;

    let nextScriptId: ScriptID | undefined;

    if (this.shouldShowWantToAddFriendCtas(script)) {
      nextScriptId = await this.showWantToAddFriendScript(script);
    } else if (scriptIsConversational(script)) {
      nextScriptId = await this.showConversationalScript(script);
    } else if (scriptIsSelectional(script)) {
      nextScriptId = await this.showSelectionalScript(script, dialogId);
    } else if (scriptIsDescriptive(script)) {
      nextScriptId = await this.showDescriptiveScript(script, dialogId);
    }

    return nextScriptId;
  }

  /**
   * Triggered before dialog show, before first script popup open
   */
  private async onDialogOpen(dialog: DialogData | null) {
    // Handle pet evolving.
    if (dialog?.pet_states?.includes('evolving')) {
      void this.pet.evolve();
    }

    // Handle pet death
    if (dialog?.pet_states?.includes('dead')) {
      void this.pet.die();
    }

    // Handle pet going to sleep
    if (dialog?.pet_states?.includes('sleeping')) {
      void this.pet.goToSleep();
    }

    // Trigger tutorial
    await getTutorialController().handleEvent('conversationStarted');
  }

  /**
   * Triggered on dialog finished, after all scripts popups was shown
   */
  private async onDialogFinish(dialog: DialogData | null) {
    // Acknowledge visit when closing had_visitor dialog
    if (dialog?.scenes?.includes('had_visitor')) {
      // TODO: After dialog close?
      void StateObserver.invoke.acknowledgeVisit();
    }

    // Resolve want_to_add_friend and open Add Friend popup
    if (dialog?.pet_states?.includes('want_to_add_friend')) {
      await StateObserver.invoke.resolveWantInvite();
      if (features(StateObserver.getState().user).petDialogAddFriendCtas) {
        if (this.wantToAddFriendResult === 'addFriend') {
          await openAddFriendDirectShare();
        }
      } else {
        await openAddFriendPopup({
          origin: 'hud',
          feature: Feature.PET_DIALOG._,
        });
      }
    }

    // Trigger tutorial
    if (await getTutorialController().handleEvent('conversationFinished')) {
      void petIdleSequence();
    }
  }

  /**
   * On this scenes is safe to start conversations
   */
  private isInConversationScene(): boolean {
    return (
      isSceneEntered('livingRoom') ||
      isSceneEntered('friendsLivingRoom') ||
      isSceneEntered('arena')
    );
  }

  /**
   * Simple conversation popup
   */
  private async showConversationalScript(script: ConversationalScript) {
    await openPopupPromise('popupConversationScript', script);

    if (script.next_script_id) {
      return script.next_script_id;
    }

    const { user } = StateObserver.getState();
    const { answers } = user.conversation;
    const { visitSession } = user.visit;

    let answerKey = script.case;
    let answer = answers[answerKey ?? '']?.key;
    if (answerKey?.includes('friend.') && visitSession) {
      answerKey = answerKey.replace('friend.', '');
      answer = visitSession.answers[answerKey]?.key;
    }

    return getNextScriptId(script, Number(answer));
  }

  /**
   * Show a conversation with a selection prompt.
   *
   * @param script Selectional script to display.
   * @param dialogId Parent dialog containing the script.
   * @returns The next script ID selected by the answer.
   */
  private async showSelectionalScript(
    script: SelectionalScript,
    dialogId: DialogID,
  ) {
    const answer = await openPopupPromise('popupSelectionalScript', script);
    const answerValue = this.getSelectionalScriptAnswer(script, answer);
    if (answerValue) {
      // Save the selected answer before emitting its analytics event.
      await StateObserver.invoke.saveConversationAnswer({
        answerVar: script.answer_var,
        key: `${answer}`,
        value: answerValue,
      });
      trackDialogAnswerEvent({
        dialogId,
        scriptId: script.id,
        answerKey: `${answer}`,
      });
    }

    const nextScriptId = getNextScriptId(script, answer);
    if (!nextScriptId) {
      const errMsg = `Next script key for answer ${answer} not found for script ${script.id}`;
      console.error(errMsg);
      captureGenericError(errMsg, null);
    }

    return nextScriptId;
  }

  private shouldShowWantToAddFriendCtas(script: Script): boolean {
    return (
      String(script.id) === 'want_to_add_friend_01' &&
      features(StateObserver.getState().user).petDialogAddFriendCtas
    );
  }

  private async showWantToAddFriendScript(script: Script) {
    this.wantToAddFriendResult = await openPopupPromise(
      'popupWantToAddFriend',
      script,
    );

    return undefined;
  }

  private getSelectionalScriptAnswer(
    script: SelectionalScript,
    answerIndex: 1 | 2 | 3 | undefined,
  ) {
    if (answerIndex === undefined) return undefined;

    const localeSuffix =
      StateObserver.getState().ui.locale === 'en' ? 'en' : 'jp';
    return script[`option_${answerIndex}_${localeSuffix}`];
  }

  /**
   * Show a conversation with a text input prompt.
   *
   * @param script Descriptive script to display.
   * @param dialogId Parent dialog containing the script.
   * @returns The next script ID in the dialog.
   */
  private async showDescriptiveScript(
    script: DescriptiveScript,
    dialogId: DialogID,
  ) {
    await openPopupPromise('popupConversationScript', script);
    const answer = await openPopupPromise('popupDescriptiveScript', script);
    if (answer) {
      // Save the entered answer before emitting its analytics event.
      await StateObserver.invoke.saveConversationAnswer({
        answerVar: script.answer_var,
        value: answer,
      });
      trackDialogAnswerEvent({
        dialogId,
        scriptId: script.id,
      });
    }
    return script.next_script_id;
  }

  public blockAutoDialog(value: boolean) {
    this.autoDialogBlocked = value;
  }

  public isAutoDialogBlocked() {
    return this.autoDialogBlocked;
  }
}
